'use client';

import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';

import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

interface ShowingTagContextType {
    showingTags: ShowingTagDto[];
    getShowingTagById: (id: ShowingTagDto['id']) => ShowingTagDto | undefined;
}

const ShowingTagContext = createContext<ShowingTagContextType | undefined>(undefined);

interface ShowingTagProviderProps {
    children: ReactNode;
    showingTags: ShowingTagDto[];
}

export function ShowingTagProvider({ children, showingTags = [] }: ShowingTagProviderProps) {
    const getShowingTagById = useCallback(
        (id: ShowingTagDto['id']): ShowingTagDto | undefined => {
            return showingTags.find((room) => room.id === id);
        },
        [showingTags]
    );

    const value: ShowingTagContextType = useMemo(
        () => ({
            showingTags,
            getShowingTagById
        }),
        [showingTags, getShowingTagById]
    );

    return <ShowingTagContext.Provider value={value}>{children}</ShowingTagContext.Provider>;
}

export function useShowingTags(): ShowingTagContextType {
    const context = useContext(ShowingTagContext);
    if (context === undefined) {
        throw new Error('useShowingTags must be used within a ShowingTagProvider');
    }
    return context;
}
